import React, { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Heading } from '@pandiafi/design-system'

import {
  AddTenantInput,
  Contract,
  ResidentAppLocale,
  UserMetadata,
} from '../../../../common/validations'
import i18n from '../../../i18n/i18n'
import { Section, TextTable } from '../../../components'
import { useAPIExecuteModificationAddTenant } from '../../../api/apiHooks/useAPIExecuteModificationAddTenant'

type Props = {
  formValues: AddTenantInput
  contract: Contract
  cancel: () => void
  onSuccess: () => void
  userMetadata: UserMetadata
}

export const AddTenantPreview: FC<Props> = ({
  formValues,
  contract,
  cancel,
  onSuccess,
  userMetadata: { isImpersonator },
}) => {
  const { t } = useTranslation(['common', 'contractModification'])

  const lang = i18n.language ?? ResidentAppLocale.FI

  const { mutate, isSuccess, data, isPending } =
    useAPIExecuteModificationAddTenant(contract.uuid, lang)

  const handleCancel = () => {
    cancel()
  }

  const handleSubmit = () => {
    mutate({
      operationData: formValues,
    })
  }

  useEffect(() => {
    if (isSuccess && data && data.success) {
      onSuccess()
    }
  })

  // We want to show the loading spinner until the user is redirected
  const isLoading = isPending || isSuccess

  return (
    <>
      <div className="bg-brand-1 py-8 rounded-t-lg -mt-7 -mx-7 mb-10 text-center">
        <Heading size="l" element="h1">
          {t('contractModification:headings.modificationPreview')}
        </Heading>
      </div>
      <Section className="mb-12">
        <TextTable className="min-tablet:text-lg">
          <TextTable.Row>
            <TextTable.Cell>
              {t('contractModification:labels.change')}
            </TextTable.Cell>
            <TextTable.Cell>
              {t('contractModification:labels.personAdded')}
            </TextTable.Cell>
          </TextTable.Row>
          {formValues.tenants.map((tenant) => (
            <>
              <TextTable.Row>
                <TextTable.Cell>
                  {t('contractModification:labels.personInformation')}
                </TextTable.Cell>
                <TextTable.Cell>{tenant.socialSecurityNumber}</TextTable.Cell>
              </TextTable.Row>
              <TextTable.Row>
                <TextTable.Cell> </TextTable.Cell>
                <TextTable.Cell>
                  {tenant.firstName} {tenant.lastName}
                </TextTable.Cell>
              </TextTable.Row>
              <TextTable.Row>
                <TextTable.Cell> </TextTable.Cell>
                <TextTable.Cell>{tenant.email}</TextTable.Cell>
              </TextTable.Row>
              <TextTable.Row>
                <TextTable.Cell> </TextTable.Cell>
                <TextTable.Cell>{tenant.phoneNumber}</TextTable.Cell>
              </TextTable.Row>
            </>
          ))}
        </TextTable>
      </Section>
      <div className="min-tablet:mt-16 min-tablet:flex min-tablet:items-center min-tablet:justify-end flex gap-4">
        <Button type="button" variant="cancel" onClick={() => handleCancel()}>
          {t('contractModification:buttons.backToEdit')}
        </Button>
        <Button
          type="button"
          variant="delete"
          onClick={() => handleSubmit()}
          disabled={isImpersonator || isLoading}
          loading={isLoading}
        >
          {t('contractModification:buttons.confirmModification')}
        </Button>
      </div>
    </>
  )
}
